import React, { useEffect, useState } from 'react';
import { FaEye, FaPen, FaBan, FaPlay, FaTrash, FaSearch, FaTimes, FaPlus, FaBullhorn } from 'react-icons/fa';
import LoadingState from '../components/LoadingState';
import { useNavigate } from 'react-router-dom';
import { listCampaignsApi, deleteCampaignApi, updateActiveState } from '../api/api';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ConfirmationModal from '../components/ConfirmationModal';

const CampaignList = ({ setAlert }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [editTarget, setEditTarget] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleEdit = (name) => setEditTarget(name);
  const confirmEdit = () => {
    navigate(`/campaigns/${encodeURIComponent(editTarget)}`);
    setEditTarget('');
  };
  const toggleRowExpansion = (campaignName) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(campaignName)) {
      newExpandedRows.delete(campaignName);
    } else {
      newExpandedRows.add(campaignName);
    }
    setExpandedRows(newExpandedRows);
  };

  const renderTemplates = (templates, campaignName) => {
    if (!templates || templates.length === 0) {
      return <span className="text-xs text-gray-400">No scorecards</span>;
    }

    const isExpanded = expandedRows.has(campaignName);
    const visible = isExpanded ? templates : templates.slice(0, 2);
    const remaining = templates.length - visible.length;

    return (
      <div className="flex items-center flex-wrap gap-1.5">
        {visible.map((t) => (
          <span
            key={t}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-coral-50 text-coral-700 border border-coral-200 text-xs font-medium"
          >
            {t}
          </span>
        ))}
        {remaining > 0 && (
          <button
            onClick={() => toggleRowExpansion(campaignName)}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium transition-colors"
            title={templates.slice(2).join(', ')}
          >
            +{remaining}
          </button>
        )}
        {isExpanded && templates.length > 2 && (
          <button
            onClick={() => toggleRowExpansion(campaignName)}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium transition-colors"
          >
            less
          </button>
        )}
      </div>
    );
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await listCampaignsApi();
        setCampaigns(res.campaigns || []);
      } catch (err) {
        console.error('Failed to load campaigns:', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load campaigns.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [setAlert]);

  const handleDelete = (name) => {
    setDeleteInfo(name);
  };

  const handleToggleActive = async (name, currentlyActive) => {
    try {
      await updateActiveState(name);
      setCampaigns((prev) =>
        prev.map((cam) =>
          cam.campaignName === name ? { ...cam, active: !currentlyActive } : cam
        )
      );
      setAlert?.({
        variant: 'success',
        heading: `${name} ${currentlyActive ? 'Deactivated' : 'Reactivated'}`,
        text: currentlyActive ? 'Campaign has been deactivated.' : 'Campaign reactivated',
      });
    } catch (err) {
      console.error('Failed to update campaign active state', err);
      setAlert?.({
        variant: 'error',
        heading: 'Error',
        text: `Failed to ${currentlyActive ? 'deactivate' : 'reactivate'} campaign.`,
      });
    }
  };

  const confirmDelete = async () => {
    const name = deleteInfo;
    try {
      const res = await deleteCampaignApi(name);
      setCampaigns((c) => c.filter((cam) => cam.campaignName !== name));

      const templatesMsg =
        res?.deletedCampaign?.templates && res.deletedCampaign.templates.length > 0
          ? ` Templates removed: ${res.deletedCampaign.templates.join(', ')}.`
          : '';

      setAlert?.({
        variant: 'success',
        heading: 'Campaign Deleted',
        text: `${res?.message || 'Campaign deleted successfully.'}${templatesMsg}`,
      });
    } catch (err) {
      console.error('Delete failed', err);
      const msg = err?.message || 'Failed to delete campaign.';
      setAlert?.({ variant: 'error', heading: 'Error', text: msg });
    } finally {
      setDeleteInfo(null);
    }
  };

  const filtered = campaigns.filter((c) =>
    c.campaignName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Campaigns</h1>
          <p className="text-gray-500 text-sm mt-1">Manage campaigns and their scorecards</p>
        </div>
        <button onClick={() => navigate('/campaigns/new')} className="btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Campaign
        </button>
      </div>
      <div className="card !p-0 overflow-hidden" data-tour="campaign-table">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full sm:max-w-xs group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-[13px] pointer-events-none transition-colors group-focus-within:text-cloudza-600" />
            <input
              type="text"
              className="input-field !rounded-full !pl-10 !pr-9 !py-2.5"
              placeholder="Search campaigns…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search campaigns"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-[11px]" />
              </button>
            )}
          </div>
          {!loading && campaigns.length > 0 && (
            <span className="text-xs text-gray-400 pr-1">
              {search ? `${filtered.length} of ${campaigns.length}` : `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`}
            </span>
          )}
        </div>
        {loading ? (
          <LoadingState variant="rows" header={false} label="Loading campaigns" />
        ) : campaigns.length === 0 ? (
          <div className="py-14 px-6 text-center">
            <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
              <FaBullhorn className="text-lg" />
            </span>
            <p className="mt-4 text-sm font-semibold text-gray-800">No campaigns yet</p>
            <p className="mt-1 text-sm text-gray-400">Create a campaign and attach the scorecards it should be assessed against.</p>
            <button onClick={() => navigate('/campaigns/new')} className="btn-primary mt-5">
              <FaPlus />
              New Campaign
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-14 px-6 text-center">
            <p className="text-sm text-gray-400">No campaigns match <span className="font-semibold text-gray-600">“{search}”</span></p>
            <button onClick={() => setSearch('')} className="btn-outline !py-1.5 !px-4 !text-xs mt-4">
              <FaTimes />
              Clear search
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Name</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Scorecards</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Status</th>
                  <th className="px-5 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(c => {
                  const isActive = c.active === true || c.active === undefined;
                  return (
                  <tr key={c.campaignName} className={`hover:bg-cloudza-50/40 transition-colors ${isActive ? '' : 'opacity-60 hover:opacity-100'}`}>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white text-xs font-bold flex items-center justify-center uppercase flex-shrink-0 shadow-sm">
                          {c.campaignName.trim().charAt(0)}
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">{c.campaignName}</div>
                          <div className="text-[11px] text-gray-400">
                            {(c.templates || []).length} scorecard{(c.templates || []).length === 1 ? '' : 's'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-gray-500">{renderTemplates(c.templates, c.campaignName)}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${isActive ? 'bg-coral-50 text-coral-700' : 'bg-gray-100 text-gray-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-coral-500' : 'bg-gray-400'}`} />
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/campaigns/view/${encodeURIComponent(c.campaignName)}`)}
                          className="btn-outline !py-1 !px-3 !text-xs"
                        >
                          <FaEye />
                          View
                        </button>
                        {isActive ? (
                          <>
                            <button
                              onClick={() => handleEdit(c.campaignName)}
                              className="btn-outline !py-1 !px-3 !text-xs"
                            >
                              <FaPen />
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleActive(c.campaignName, true)}
                              className="btn-outline !py-1 !px-3 !text-xs !text-amber-700 !border-amber-200 hover:!bg-amber-50"
                            >
                              <FaBan />
                              Deactivate
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleToggleActive(c.campaignName, false)}
                              className="btn-outline !py-1 !px-3 !text-xs !text-coral-700 !border-coral-200 hover:!bg-coral-50"
                            >
                              <FaPlay />
                              Re-activate
                            </button>
                            <button
                              onClick={() => handleDelete(c.campaignName)}
                              className="btn-outline !py-1 !px-3 !text-xs !text-red-600 !border-red-200 hover:!bg-red-50"
                            >
                              <FaTrash />
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmationModal
        open={!!editTarget}
        title="Edit Confirmation"
        message={`Are you sure you want to enter edit mode for ${editTarget}?`}
        confirmText="Continue"
        onClose={() => setEditTarget('')}
        onConfirm={confirmEdit}
      />
      <DeleteConfirmationModal
        open={!!deleteInfo}
        itemName={deleteInfo || ''}
        onClose={() => setDeleteInfo(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default CampaignList;

